// native
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subject, Observable, of, from } from 'rxjs';
import { switchMap, startWith, pairwise, map, tap } from 'rxjs/operators';

// service
import { GlobalsService } from './globals.service';
import { ItemsSyncService } from './items-sync.service';
import { ItemsDisplayService } from './items-display.service';
import { ToastService } from './toast.service';
import { BulkCreateItemService } from './bulk-create.service';
import { CitationsQueueService } from './citations-queue.service';
import { UtilityService } from './utility.service';
import { StylesService } from './styles.service';

// model
import { CitationSelection, DocumentItemsResponse, IDocumentStyle, IDisplayItem, ICitationSelection, IItem, ReformatPayload, DocumentCitations } from '../../models';

// constants
import { INVALID_SELECTION_ERROR, BIBLIO_SELECTION_ERROR, INVALID_CURSOR_ERROR, ONLINE_UNSUPPORTED_ERROR } from '../../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class CitationsEventService {

  isCitationSelected: boolean = false;

  isCitationDeselected: boolean = false;

  // called when anything is selected inside the document and used to map select actions in sequence
  selection$: Subject<CitationSelection> = new Subject<CitationSelection>();

  // called after selection gets through sequence filter and used for citation selection and deselection logic
  // if citation is selected it will emit items, else null. it will also emit info about deselection
  citationSelected$: BehaviorSubject<ICitationSelection> = new BehaviorSubject<ICitationSelection>({ items: null, isCitationDeselected: false });

  citationQueueLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // called when item is checked in the list
  // on call citation drawer is shown and citation queue is updated
  itemAddedToCitationQueue$: Subject<IDisplayItem> = new Subject<IDisplayItem>();

  // called when item is removed from the citation queue
  // on call item is unchecked in the list
  itemRemovedFromCitationQueue$: Subject<IDisplayItem> = new Subject<IDisplayItem>();

  // called when item is unchecked in the list
  // on call item is removed from citation queue
  itemUncheckedFromList$: Subject<IDisplayItem> = new Subject<IDisplayItem>();

  // called when citation is added/updated or when last pending citation is removed from item list or pending citation list
  // on call citation drawer is removed and citation queue is empty
  clearCitationQueue$: Subject<void> = new Subject<void>();

  // called inside citation queue container
  // on call used in references to calculate heights
  citationContainerOffsetHeight$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  processingAddUpdateCitation$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private citationsQueueService: CitationsQueueService,
    private itemsSyncService: ItemsSyncService,
    private globalsService: GlobalsService,
    private itemsDisplayService: ItemsDisplayService,
    private toastService: ToastService,
    private bulkCreateItemService: BulkCreateItemService,
    private utilityService: UtilityService,
    private stylesService: StylesService,
    private ngZone: NgZone
  ) {
    this.setSelectionSubscription();
  }

  private setSelectionSubscription() {
    this.selection$.pipe(
      startWith(null),
      pairwise(),
      map(([previousSelection, currentSelection]) => this.checkForDeselection(previousSelection, currentSelection)),
      tap((citation: CitationSelection | null) => {
        this.isCitationSelected = !!citation?.ids?.length;
      }),
      switchMap((citation: CitationSelection | null) => this.getItems$(citation))
    ).subscribe((response: DocumentItemsResponse) => {
      this.ngZone.run(() => {
        this.citationQueueLoading$.next(false);
        this.itemsDisplayService.itemPanelVisible$.next(false);
        this.itemsDisplayService.optionsPanelVisible$.next(false);
        this.citationsQueueService.overridePanelVisible$.next(false);

        if (!response || !response.items) {
          this.citationSelected$.next({ items: null, isCitationDeselected: this.isCitationDeselected });
          return;
        }

        if (response.nullItemsCount && response.nullItemsCount > 0) {
          this.showNullCitationsToast(response.items, response.nullItemsCount, response.options);
          return;
        }

        this.citationSelected$.next({ items: response.items, isCitationDeselected: this.isCitationDeselected, options: response.options });
      });
    }, error => {
      this.citationQueueLoading$.next(false);
      this.citationSelected$.next({ items: null, isCitationDeselected: this.isCitationDeselected });
    });
  }

  private checkForDeselection(previousSelection: CitationSelection | null, currentSelection: CitationSelection | null) {
    this.isCitationDeselected = !!previousSelection;

    return currentSelection;
  }

  private getItems$(documentCitation: CitationSelection): Observable<DocumentItemsResponse> {
    if (!this.isCitationSelected) {
      return of(null);
    }

    this.ngZone.run(() => this.citationQueueLoading$.next(true));

    return from(this.itemsSyncService.getItemsByDocumentCitations(documentCitation));
  }

  private showNullCitationsToast(items: IDisplayItem[], nullItemsCount: number, options: any) {
    const confirmCallback = () => {
      this.citationSelected$.next({ items: items, isCitationDeselected: this.isCitationDeselected, options: options });
    };

    const cancelCallback = () => {
      this.citationSelected$.next({ items: null, isCitationDeselected: this.isCitationDeselected });
    };

    this.toastService.openToast({
      text: `This citation contains reference${nullItemsCount > 1 ? 's' : ''} to ${nullItemsCount} paper${nullItemsCount > 1 ? 's' : ''} 
            that ${nullItemsCount > 1 ? 'have' : 'has'} been removed from user library. 
            If you update the bibliography, ${nullItemsCount > 1 ? 'those' : 'that'} reference${nullItemsCount > 1 ? 's' : ''} will be removed from the document.`,
      type: 'error',
      confirmButton: {
        text: 'OK',
        callback: confirmCallback
      },
      cancelButton: {
        text: 'Cancel',
        callback: cancelCallback
      },
      autoClose: false,
      closeDisabled: true
    });
  }

  setSelectedCitation(citation: CitationSelection | null) {
    this.selection$.next(citation);
  }

  async addCitation(isUpdate: boolean, isOverrideReset?: boolean): Promise<void> {
    this.itemsDisplayService.itemsLoading$.next(true);
    this.processingAddUpdateCitation$.next(true);

    try {
      // first we inspect are there items that need to be added to library before proceeding with citation
      // after creation, those items are merged back to citation queue array based on client id (which can be changed in the response) 
      // and the fact that bulk create response always returns same number and order of items as in the request
      const itemsToCreate = this.citationsQueueService.citationQueueItems.filter((item: IDisplayItem) => {
        return !item.collection_id;
      });

      if (itemsToCreate && itemsToCreate.length) {
        const createdItems: IItem[] = await this.bulkCreateItemService.bulkCreateItems(itemsToCreate);

        itemsToCreate.forEach((item: IItem, index: number) => {
          const citationQueueIndex = this.utilityService.findArrayMemberIndexWithProp(this.citationsQueueService.citationQueueItems, 'id', item.id);

          this.citationsQueueService.citationQueueItems[citationQueueIndex] = createdItems[index];
        });
      }

      // only items with unique collection id + item id combinations are passed to document 
      // (covering the web search edge case where same articles can be repeatedly added resulting in multiple items with same id)
      const uniqueItems: IDisplayItem[] = this.removeItemDuplicates(this.citationsQueueService.citationQueueItems);

      const style: IDocumentStyle = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();

      const addUpdatePayload: ReformatPayload | void = isUpdate
        ? await this.globalsService.appConfig.updateCitation(
          uniqueItems,
          style,
          this.globalsService.locale,
          this.globalsService.language,
          isOverrideReset ? false : this.globalsService.isBibliographyAutoUpdateOn,
          this.citationsQueueService.citationQueueOptions,
          this.globalsService.isHyperlinksModeOn,
          this.globalsService.isSectionsModeOn)
        : await this.globalsService.appConfig.addCitation(
          uniqueItems,
          style,
          this.globalsService.locale,
          this.globalsService.language,
          isOverrideReset ? false : this.globalsService.isBibliographyAutoUpdateOn,
          this.citationsQueueService.citationQueueOptions,
          this.globalsService.isHyperlinksModeOn,
          this.globalsService.isSectionsModeOn);

      if ((<ReformatPayload>addUpdatePayload)?.numCslErrCits)
        this.toastService.showCslError((<ReformatPayload>addUpdatePayload).numCslErrCits);
      else if (isOverrideReset)
        this.toastService.showOverrideResetInfo();

      this.itemsDisplayService.itemsLoading$.next(false);
      this.processingAddUpdateCitation$.next(false);

      this.isCitationSelected = false;
      this.globalsService.appConfig.clearCitationQueueOnAddUpdate && this.clearCitationQueue$.next();
    } catch (err) {
      this.itemsDisplayService.itemsLoading$.next(false);
      this.processingAddUpdateCitation$.next(false);

      if (err?.message?.includes && err.message.includes(ONLINE_UNSUPPORTED_ERROR))
        return this.toastService.showWordOnlineError();

      switch (err) {
        case INVALID_CURSOR_ERROR:
          this.toastService.openToast({
            text: `Unfortunately, we can't insert citation on your current cursor position. Please try to deselect or move your cursor.`,
            type: 'error',
            closeDelay: 6000
          });
          break;
        case INVALID_SELECTION_ERROR:
          this.toastService.openToast({
            text: `Unfortunately, we can't replace current selection with citation. Please try to deselect or move your cursor.`,
            type: 'error',
            closeDelay: 6000
          });
          break;
        case BIBLIO_SELECTION_ERROR:
          this.toastService.openToast({
            text: `Cursor or selection detected in the bibliography. Citation insertion inside the bibliography is not supported. 
              Please move your cursor to a different location.`,
            type: 'error',
            closeDelay: 6000
          });
          break;
        default:
          this.itemsDisplayService.itemsVisible$.next(false);
          this.isCitationSelected = false;
          this.clearCitationQueue$.next();
          this.toastService.showGenericError();
      }
    }
  }

  private removeItemDuplicates(originalItems: IDisplayItem[]): IDisplayItem[] {
    const mappedProps = originalItems.map(item => item.collection_id + item.id);

    return originalItems.filter((item, index) => {
      if (!item || !item.collection_id || !item.id)
        return true;
      return mappedProps.indexOf(item.collection_id + item.id) === index;
    });
  }

  getDocumentCitations(): Promise<DocumentCitations> {
    return this.globalsService.appConfig.getCitations();
  }
}

